import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { WarningAmber as WarningAmberIcon, CreditCard as CreditCardIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { useAppData } from '../../context/AppDataContext';
import { usePermissions } from '../../hooks/usePermissions';
import useBillingPortalLauncher from '../../hooks/useBillingPortalLauncher';
import useCheckoutLauncher from '../../hooks/useCheckoutLauncher';
import StoreService from '../../services/storeService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getFunctionalItem, setFunctionalItem, removeFunctionalItem } from '../../utils/functionalStorage';
import { normalizeEntitlements } from '../../utils/storePlan';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const bannerBorderRadius = BRAND_RADIUS.card;
const billingStatusCacheTtlMs = 60000;
const getBillingReminderDismissalKey = (storeId) => `billing-reminder-dismissed:${storeId}`;
const getBillingStatusRequestKey = (storeId) => `store-billing-status:${storeId}`;

const billingStatusCache = new Map();

const shouldShowPaymentFailedBanner = (billingStatus) => Boolean(
  billingStatus?.showPaymentFailedBanner || billingStatus?.isInPaymentGrace,
);

const shouldShowReactivateNudge = (billingStatus) => Boolean(
  billingStatus?.showReactivateNudge || billingStatus?.entitlements?.showReactivateNudge,
);

const shouldShowBillingReminder = (billingStatus) => {
  if (!billingStatus) {
    return false;
  }

  if (shouldShowPaymentFailedBanner(billingStatus) || shouldShowReactivateNudge(billingStatus)) {
    return true;
  }

  return billingStatus.requiresPaymentMethod === true;
};

const normalizeBillingStatus = (result) => {
  const entitlements = normalizeEntitlements(result?.entitlements ?? result?.Entitlements ?? {
    effectivePlan: result?.effectivePlan ?? result?.EffectivePlan,
    isEffectivePro: result?.isEffectivePro ?? result?.IsEffectivePro,
    isLapsedPro: result?.isLapsedPro ?? result?.IsLapsedPro,
    isInPaymentGrace: result?.isInPaymentGrace ?? result?.IsInPaymentGrace,
    showPaymentFailedBanner: result?.showPaymentFailedBanner ?? result?.ShowPaymentFailedBanner,
    showReactivateNudge: result?.showReactivateNudge ?? result?.ShowReactivateNudge,
    reactivateNudgeStage: result?.reactivateNudgeStage ?? result?.ReactivateNudgeStage,
    proLapsedAtUtc: result?.proLapsedAtUtc ?? result?.ProLapsedAtUtc,
  });

  return {
    hasPaymentDetails: Boolean(result?.hasPaymentDetails ?? result?.HasPaymentDetails),
    requiresPaymentMethod: Boolean(result?.requiresPaymentMethod ?? result?.RequiresPaymentMethod),
    currentPlan: result?.currentPlan ?? result?.CurrentPlan ?? null,
    effectivePlan: result?.effectivePlan ?? result?.EffectivePlan ?? entitlements.effectivePlan,
    isEffectivePro: Boolean(result?.isEffectivePro ?? result?.IsEffectivePro ?? entitlements.isEffectivePro),
    isLapsedPro: Boolean(result?.isLapsedPro ?? result?.IsLapsedPro ?? entitlements.isLapsedPro),
    isInPaymentGrace: Boolean(result?.isInPaymentGrace ?? result?.IsInPaymentGrace ?? entitlements.isInPaymentGrace),
    showPaymentFailedBanner: Boolean(
      result?.showPaymentFailedBanner ?? result?.ShowPaymentFailedBanner ?? entitlements.showPaymentFailedBanner,
    ),
    showReactivateNudge: Boolean(
      result?.showReactivateNudge ?? result?.ShowReactivateNudge ?? entitlements.showReactivateNudge,
    ),
    reactivateNudgeStage: result?.reactivateNudgeStage
      ?? result?.ReactivateNudgeStage
      ?? entitlements.reactivateNudgeStage,
    entitlements,
  };
};

const getCachedBillingStatus = (storeId) => {
  if (!storeId || !billingStatusCache.has(storeId)) {
    return null;
  }

  const cachedEntry = billingStatusCache.get(storeId);
  if (cachedEntry.expiresAt <= Date.now()) {
    billingStatusCache.delete(storeId);
    return null;
  }

  return cachedEntry.value;
};

const setCachedBillingStatus = (storeId, billingStatus) => {
  if (!storeId || !billingStatus) {
    return;
  }

  billingStatusCache.set(storeId, {
    value: billingStatus,
    expiresAt: Date.now() + billingStatusCacheTtlMs
  });
};

const readBillingReminderDismissal = (storeId) => {
  if (!storeId || typeof window === 'undefined') {
    return false;
  }

  try {
    return getFunctionalItem(getBillingReminderDismissalKey(storeId)) === 'true';
  } catch (error) {
    console.warn('Failed to read billing reminder dismissal state:', error);
    return false;
  }
};

const writeBillingReminderDismissal = (storeId, dismissed) => {
  if (!storeId || typeof window === 'undefined') {
    return;
  }

  try {
    if (dismissed) {
      setFunctionalItem(getBillingReminderDismissalKey(storeId), 'true');
      return;
    }

    removeFunctionalItem(getBillingReminderDismissalKey(storeId));
  } catch (error) {
    console.warn('Failed to persist billing reminder dismissal state:', error);
  }
};

const BillingReminderBanner = ({ maxWidth = '1200px' }) => {
  const { t } = useTranslation();
  const { currentStore } = useAppData();
  const { isOwner } = usePermissions(currentStore?.id);
  const { openBillingPortal, loading: billingPortalLoading } = useBillingPortalLauncher(currentStore?.id);
  const { startCheckout, loading: checkoutLoading } = useCheckoutLauncher(currentStore?.id);
  const [billingStatus, setBillingStatus] = useState(() => getCachedBillingStatus(currentStore?.id));
  const [billingStatusLoading, setBillingStatusLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [nudgeDismissing, setNudgeDismissing] = useState(false);

  useEffect(() => {
    setDismissed(readBillingReminderDismissal(currentStore?.id));
  }, [currentStore?.id]);

  useEffect(() => {
    let disposed = false;

    const loadBillingStatus = async () => {
      if (!currentStore?.id || !isOwner) {
        setBillingStatus(null);
        setBillingStatusLoading(false);
        return;
      }

      const cachedBillingStatus = getCachedBillingStatus(currentStore.id);
      if (cachedBillingStatus) {
        setBillingStatus(cachedBillingStatus);
        setBillingStatusLoading(false);
        return;
      }

      try {
        setBillingStatus(null);
        setBillingStatusLoading(true);
        const result = await requestDeduplicator.deduplicate(
          getBillingStatusRequestKey(currentStore.id),
          () => StoreService.getBillingStatus(currentStore.id)
        );
        const normalizedResult = normalizeBillingStatus(result);
        setCachedBillingStatus(currentStore.id, normalizedResult);

        if (!disposed) {
          setBillingStatus(normalizedResult);
        }
      } catch (error) {
        console.error('Failed to load billing status:', error);

        if (!disposed) {
          setBillingStatus(null);
        }
      } finally {
        if (!disposed) {
          setBillingStatusLoading(false);
        }
      }
    };

    loadBillingStatus();

    return () => {
      disposed = true;
    };
  }, [currentStore?.id, isOwner]);

  useEffect(() => {
    const requiresBillingAttention = shouldShowBillingReminder(billingStatus);

    if (!currentStore?.id || requiresBillingAttention) {
      return;
    }

    writeBillingReminderDismissal(currentStore.id, false);
    setDismissed(false);
  }, [currentStore?.id, billingStatus]);

  const handleDismiss = async () => {
    if (!currentStore?.id) {
      return;
    }

    if (shouldShowReactivateNudge(billingStatus)) {
      const stage = billingStatus.reactivateNudgeStage ?? billingStatus.entitlements?.reactivateNudgeStage;
      if (stage == null) {
        return;
      }

      try {
        setNudgeDismissing(true);
        await StoreService.dismissReactivateNudge(currentStore.id, stage);
        setBillingStatus((current) => (
          current
            ? {
              ...current,
              showReactivateNudge: false,
              reactivateNudgeStage: null,
              entitlements: current.entitlements
                ? { ...current.entitlements, showReactivateNudge: false, reactivateNudgeStage: null }
                : current.entitlements,
            }
            : current
        ));
        billingStatusCache.delete(currentStore.id);
      } catch (error) {
        console.error('Failed to dismiss reactivate nudge:', error);
      } finally {
        setNudgeDismissing(false);
      }
      return;
    }

    writeBillingReminderDismissal(currentStore.id, true);
    setDismissed(true);
  };

  const requiresBillingAttention = shouldShowBillingReminder(billingStatus);
  const showPaymentFailed = shouldShowPaymentFailedBanner(billingStatus);
  const showReactivate = shouldShowReactivateNudge(billingStatus);

  const showBillingReminder = Boolean(
    currentStore?.id &&
    isOwner &&
    requiresBillingAttention &&
    (showReactivate || showPaymentFailed || !dismissed)
  );

  const billingTitleKey = showReactivate
    ? 'storePlan.reactivateTitle'
    : showPaymentFailed
      ? 'storePlan.paymentFailedTitle'
      : billingStatus?.requiresPaymentMethod === true
        ? 'stores.billing.issueTitle'
        : 'stores.billing.title';

  const billingDescriptionKey = showReactivate
    ? 'storePlan.reactivateDescription'
    : showPaymentFailed
      ? 'storePlan.paymentFailedDescription'
      : billingStatus?.requiresPaymentMethod === true
        ? 'stores.billing.issueDescription'
        : 'stores.billing.description';

  const actionLabel = showReactivate
    ? (checkoutLoading ? t('stores.checkout.opening') : t('storePlan.reactivateAction'))
    : showPaymentFailed
      ? (billingPortalLoading ? t('stores.billing.opening') : t('storePlan.paymentFailedAction'))
      : (billingPortalLoading ? t('stores.billing.opening') : t('stores.billing.action'));

  const handleAction = showReactivate ? startCheckout : openBillingPortal;
  const actionLoading = showReactivate ? checkoutLoading : billingPortalLoading;

  if (!showBillingReminder) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth,
        mx: 'auto',
        mb: { xs: 2.5, md: 3 }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 1.5 },
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          borderRadius: bannerBorderRadius,
          border: `1px solid ${BRAND.line}`,
          background: BRAND.surface,
          boxShadow: `0 12px 30px ${brandAlpha('primaryDark', 0.18)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: BRAND.text,
                fontWeight: 800,
                lineHeight: 1.25,
                mb: { xs: 0, sm: 0.25 }
              }}
            >
              {t(billingTitleKey)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: { xs: 'none', sm: 'block' },
                lineHeight: 1.35
              }}
            >
              {t(billingDescriptionKey)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            alignSelf: { xs: 'stretch', sm: 'center' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <PrimaryActionButton
            size="small"
            onClick={handleAction}
            disabled={actionLoading || billingStatusLoading || nudgeDismissing}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon sx={{ fontSize: 18 }} />}
            sx={{
              flex: { xs: 1, sm: '0 0 auto' },
              alignSelf: { xs: 'stretch', sm: 'center' },
              minWidth: 0,
              px: { xs: 1.5, sm: 1.75 },
              py: 0.8,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {actionLabel}
          </PrimaryActionButton>

          <IconButton
            size="small"
            aria-label={t('common.close')}
            onClick={handleDismiss}
            disabled={nudgeDismissing}
            sx={{
              flexShrink: 0,
              color: BRAND.textMuted,
              border: `1px solid ${BRAND.line}`,
              borderRadius: BRAND_RADIUS.button,
              bgcolor: BRAND.surface,
              '&:hover': {
                color: BRAND.accentText,
                bgcolor: brandAlpha('accent', 0.06)
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default BillingReminderBanner;